import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CLINICAL_STEPS } from "@/components/chain/chainData";
import { Reveal } from "./Reveal";

function useAutoAdvance(count, paused) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.35 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!inView || paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 4200);
    return () => clearInterval(t);
  }, [inView, paused, count]);
  return { ref, idx, setIdx };
}

// Animated "Our Clinical Process" story: Listen → Assess → Find the Pattern → Treat → Reassess → Restore.
export function ClinicalProcess({ bg = "bg-ivory", testid = "clinical-process" }) {
  const [paused, setPaused] = useState(false);
  const { ref, idx, setIdx } = useAutoAdvance(CLINICAL_STEPS.length, paused);
  const step = CLINICAL_STEPS[idx];
  const pick = (i) => { setPaused(true); setIdx(i); };

  return (
    <section ref={ref} className={`${bg} py-24 lg:py-36 px-6 md:px-10 lg:px-14`} data-testid={testid}>
      <div className="max-w-[1500px] mx-auto">
        <Reveal>
          <div className="overline mb-5">Our Clinical Process</div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-espresso max-w-3xl leading-[1.05]">
            Six steps. <span className="italic text-taupe">One connected story.</span>
          </h2>
        </Reveal>
        <div className="mt-14 relative">
          <div className="absolute left-0 right-0 top-[11px] h-px bg-espresso/15 hidden md:block" />
          <motion.div className="absolute left-0 top-[11px] h-px bg-brass hidden md:block" animate={{ width: `${(idx / CLINICAL_STEPS.length) * 100}%` }} transition={{ duration: 0.8, ease: "easeInOut" }} />
          <div className="grid grid-cols-3 md:grid-cols-6 gap-y-8 gap-x-4">
            {CLINICAL_STEPS.map((s, i) => (
              <button key={s.name} onClick={() => pick(i)} data-testid={`${testid}-step-${i}`} className="relative text-left group flex flex-col items-start self-start">
                <span className={`block h-[23px] w-[23px] rounded-full border transition-all duration-500 ${i <= idx ? "bg-brass border-brass" : "bg-ivory border-espresso/25 group-hover:border-espresso"}`} />
                <span className="mt-4 block text-xs text-taupe">{String(i + 1).padStart(2, "0")}</span>
                <span className={`block font-serif text-xl lg:text-2xl transition-colors duration-300 ${i === idx ? "text-espresso" : "text-espressosoft/50 group-hover:text-espresso"}`}>{s.name}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="mt-14 min-h-[220px]">
          <AnimatePresence mode="wait">
            <motion.div key={step.name} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.5 }} className="grid lg:grid-cols-12 gap-8" data-testid={`${testid}-detail`}>
              <h3 className="lg:col-span-5 font-serif font-light text-3xl lg:text-5xl text-espresso leading-tight">{step.title}</h3>
              <div className="lg:col-span-7">
                <p className="text-lg font-light leading-relaxed text-espressosoft/85 max-w-2xl">{step.body}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {step.tags.map((t) => <span key={t} className="rounded-full border border-espresso/15 px-4 py-1.5 text-xs uppercase tracking-[0.14em] text-taupe">{t}</span>)}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
